import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import { orderGroupIdZodSchema } from "@@/shared/order-groups/order-group-id";
import { payRequestRecipientZodSchema } from "@@/shared/pay-requests/pay-request-recipient";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { generateId } from "@@/utilities/generate-id";
import { z } from "zod";
import { orderIdZodSchema } from "../order-id";

const orderPaymentMethodSwitch_SwitchToWaivedCommandZodObject = z.object({
    _id: idZodSchema,
    _type: z.literal("ORDER_PAYMENT_METHOD_SWITCH_COMMAND"),
    _createdAt: isoAndUnixZodSchema,
    type: z.literal("SWITCH_TO_WAIVED"),
    orderGroupId: orderGroupIdZodSchema,
    orderId: orderIdZodSchema,
    providerId: providerIdZodSchema,
});
type OrderPaymentMethodSwitch_SwitchToWaivedCommand = z.infer<
    typeof orderPaymentMethodSwitch_SwitchToWaivedCommandZodObject
>;

const orderPaymentMethodSwitch_SwitchToPayRequestCommandZodObject = z.object({
    _id: idZodSchema,
    _type: z.literal("ORDER_PAYMENT_METHOD_SWITCH_COMMAND"),
    _createdAt: isoAndUnixZodSchema,
    type: z.literal("SWITCH_TO_PAY_REQUEST"),
    recipients: z.array(payRequestRecipientZodSchema).min(1),
    orderGroupId: orderGroupIdZodSchema,
    orderId: orderIdZodSchema,
    providerId: providerIdZodSchema,
});
type OrderPaymentMethodSwitch_SwitchToPayRequestCommand = z.infer<
    typeof orderPaymentMethodSwitch_SwitchToPayRequestCommandZodObject
>;

const orderPaymentMethodSwitch_SwitchToOnPremisePaidCommandZodObject = z.object(
    {
        _id: idZodSchema,
        _type: z.literal("ORDER_PAYMENT_METHOD_SWITCH_COMMAND"),
        _createdAt: isoAndUnixZodSchema,
        type: z.literal("SWITCH_TO_ON_PREMISE_PAID"),
        orderGroupId: orderGroupIdZodSchema,
        orderId: orderIdZodSchema,
        providerId: providerIdZodSchema,
    },
);
type OrderPaymentMethodSwitch_SwitchToOnPremisePaidCommand = z.infer<
    typeof orderPaymentMethodSwitch_SwitchToOnPremisePaidCommandZodObject
>;

const orderPaymentMethodSwitch_SwitchToOnPremisePayLaterCommandZodObject =
    z.object({
        _id: idZodSchema,
        _type: z.literal("ORDER_PAYMENT_METHOD_SWITCH_COMMAND"),
        _createdAt: isoAndUnixZodSchema,
        type: z.literal("SWITCH_TO_ON_PREMISE_PAY_LATER"),
        orderGroupId: orderGroupIdZodSchema,
        orderId: orderIdZodSchema,
        providerId: providerIdZodSchema,
        reason: z.string().optional(),
    });
type OrderPaymentMethodSwitch_SwitchToOnPremisePayLaterCommand = z.infer<
    typeof orderPaymentMethodSwitch_SwitchToOnPremisePayLaterCommandZodObject
>;

const orderPaymentMethodSwitchCommandZodObject = z.discriminatedUnion("type", [
    orderPaymentMethodSwitch_SwitchToPayRequestCommandZodObject,
    orderPaymentMethodSwitch_SwitchToOnPremisePaidCommandZodObject,
    orderPaymentMethodSwitch_SwitchToOnPremisePayLaterCommandZodObject,
    orderPaymentMethodSwitch_SwitchToWaivedCommandZodObject,
]);
type OrderPaymentMethodSwitchCommand = z.infer<
    typeof orderPaymentMethodSwitchCommandZodObject
>;

const orderPaymentMethodSwitchCommandFactoryParamsZodObject = z
    .discriminatedUnion("type", [
        orderPaymentMethodSwitch_SwitchToPayRequestCommandZodObject.omit({
            _id: true,
            _type: true,
            _createdAt: true,
            orderGroupId: true,
            orderId: true,
            providerId: true,
        }),
        orderPaymentMethodSwitch_SwitchToOnPremisePaidCommandZodObject.omit({
            _id: true,
            _type: true,
            _createdAt: true,
            orderGroupId: true,
            orderId: true,
            providerId: true,
        }),
        orderPaymentMethodSwitch_SwitchToOnPremisePayLaterCommandZodObject.omit(
            {
                _id: true,
                _type: true,
                _createdAt: true,
                orderGroupId: true,
                orderId: true,
                providerId: true,
            },
        ),
        orderPaymentMethodSwitch_SwitchToWaivedCommandZodObject.omit({
            _id: true,
            _type: true,
            _createdAt: true,
            orderGroupId: true,
            orderId: true,
            providerId: true,
        }),
    ])
    .and(
        z.object({
            order: z.object({
                _id: orderIdZodSchema,
                orderGroupId: orderGroupIdZodSchema,
                providerId: providerIdZodSchema,
            }),
        }),
    );

type FactoryParams = z.infer<
    typeof orderPaymentMethodSwitchCommandFactoryParamsZodObject
>;
const orderPaymentMethodSwitchCommandFactory = (params: FactoryParams) => {
    const { order, ...rest } = params;
    const command: OrderPaymentMethodSwitchCommand = {
        ...rest,
        _id: generateId({ prefix: "order_payment_method_switch_command__" }),
        _type: "ORDER_PAYMENT_METHOD_SWITCH_COMMAND",
        _createdAt: nowIsoAndUnix(),
        orderGroupId: order.orderGroupId,
        orderId: order._id,
        providerId: order.providerId,
    };
    return command;
};

export {
    orderPaymentMethodSwitch_SwitchToOnPremisePaidCommandZodObject,
    orderPaymentMethodSwitch_SwitchToOnPremisePayLaterCommandZodObject,
    orderPaymentMethodSwitch_SwitchToPayRequestCommandZodObject,
    orderPaymentMethodSwitch_SwitchToWaivedCommandZodObject,
    orderPaymentMethodSwitchCommandFactory,
    orderPaymentMethodSwitchCommandFactoryParamsZodObject,
    orderPaymentMethodSwitchCommandZodObject,
};
export type {
    OrderPaymentMethodSwitch_SwitchToOnPremisePaidCommand,
    OrderPaymentMethodSwitch_SwitchToOnPremisePayLaterCommand,
    OrderPaymentMethodSwitch_SwitchToPayRequestCommand,
    OrderPaymentMethodSwitch_SwitchToWaivedCommand,
    OrderPaymentMethodSwitchCommand,
    FactoryParams as OrderPaymentMethodSwitchCommandFactoryParams,
};
